import React, { useEffect, useState, CSSProperties } from 'react';
import classnames from 'classnames';
import { Chip } from 'app/components';
import {
  getBootstrapApp,
  getBusinessArea,
  getCurrentFile,
  getService,
  getShowHighlights,
  setCurrentFile,
  setRegenerateExtraction,
  setShowHighlights,
  useAppDispatch,
  useAppSelector
} from 'app/redux';
import PDFFileIcon from 'app/assets/Icons/PDFFile.svg';
import MP3FileIcon from 'app/assets/Icons/MP3File.svg';
import DOCFileIcon from 'app/assets/Icons/DOCFile.svg';
import DocumentBlankIcon from 'app/assets/Icons/DocumentBlank.svg';
import CSVFileIcon from 'app/assets/Icons/CSVFile.svg';

import CheckIcon from 'app/assets/Icons/Check.svg';
import DeleteIcon from 'app/assets/Icons/Delete.svg';
import { ActionButtons } from 'app/components/ActionButtons/ActionButtons';

import styles from './UploadedDocument.module.scss';
import { SERVICE_NAMES } from 'app/utils/consts';

import { formatBytes } from 'app/utils';

interface UploadedFileCardProps {
  readonly file: File;
  readonly showProgress?: boolean;
  readonly processTime?: number;
  readonly lenghtTextDisplay?: number;
  readonly showDelete?: boolean;
  readonly handleDeleteFile?: () => void;
  readonly borderRadiusRounded?: boolean;
  readonly styleFileOptions?: CSSProperties;
  readonly fileSize?: number;
}

export function UploadedFileCard({
  file,
  processTime = 0,
  showProgress = false,
  lenghtTextDisplay = 35,
  showDelete = false,
  handleDeleteFile,
  borderRadiusRounded,
  styleFileOptions,
  fileSize
}: UploadedFileCardProps): React.JSX.Element {
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(showProgress);

  const getFileIcon = (fileType: string) => {
    switch (true) {
      case fileType.includes('audio/'):
        return MP3FileIcon;
      case fileType.includes('application/msword') ||
        fileType.includes(
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ):
        return DOCFileIcon;
      case fileType.includes('application/vnd.ms-excel') ||
        fileType.includes(
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) ||
        fileType.includes('text/csv'):
        return CSVFileIcon;
      case fileType.includes('application/pdf'):
        return PDFFileIcon;
      default:
        return DocumentBlankIcon;
    }
  };

  const renderDeleteButtonOrTick = (): React.JSX.Element => {
    if (showDelete) {
      return (
        <button
          type="button"
          onClick={handleDeleteFile}
          className={styles.delete}
          aria-label="Delete uploaded file"
        >
          <img src={DeleteIcon} alt="" aria-hidden />
        </button>
      );
    }
    return <img src={CheckIcon} alt="" aria-hidden className={styles.check} />;
  };

  useEffect(() => {
    if (showProgress) {
      const interval = setInterval(() => {
        setProgress((prevProgress) => {
          const newProgress = prevProgress + 99 / (processTime / 1000);
          if (newProgress >= 99) {
            clearInterval(interval);
            setCompleted(true);
            return 99;
          }
          return newProgress;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [showProgress, processTime]);

  return (
    <div
      className={styles.file}
      style={
        borderRadiusRounded
          ? {
              padding: '1rem 0',
              margin: '1rem 0 2.375rem',
              width: '100%',
              maxWidth: '100%',
              borderRadius: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignContent: 'center',
              flexWrap: 'wrap',
              justifyContent: 'center',
              ...styleFileOptions
            }
          : styleFileOptions
      }
    >
      <div className={styles.details}>
        {showDelete ? (
          <img
            src={CheckIcon}
            alt=""
            aria-hidden
            className={styles['check-uploaded']}
          />
        ) : (
          <img
            src={getFileIcon(file.type)}
            alt=""
            aria-hidden
            className={styles.icon}
          />
        )}
        <p
          className={classnames({
            [styles.filename]: file?.name.length > lenghtTextDisplay
          })}
        >
          {file?.name.length > lenghtTextDisplay
            ? file?.name.slice(0, lenghtTextDisplay) + '...'
            : file?.name}
          {file?.name.length > lenghtTextDisplay && (
            <span className={styles.tooltip}>{file?.name}</span>
          )}
        </p>

        {completed ? (
          <p className={styles.check}>{progress.toFixed(1)}%</p>
        ) : (
          renderDeleteButtonOrTick()
        )}
      </div>

      {fileSize && (
        <div className={styles['file-size']}>{formatBytes(file.size)}</div>
      )}

      {showProgress ? (
        <progress value={progress} max={100}></progress>
      ) : (
        <span
          className={styles.loaded}
          style={
            borderRadiusRounded ? { width: '90%', marginTop: '0.3rem' } : {}
          }
        ></span>
      )}
    </div>
  );
}

export const UploadedDocument: React.FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const currentFile = useAppSelector(getCurrentFile);
  const businessArea = useAppSelector(getBusinessArea);
  const service = useAppSelector(getService);
  const bootstrap = useAppSelector(getBootstrapApp);
  const services = bootstrap.services;
  const businessAreas = bootstrap.businessAreas;
  const showHighlights = useAppSelector(getShowHighlights);
  const usesHighlights = [
    SERVICE_NAMES.CONTRACT_INSIGHTS,
    SERVICE_NAMES.CLAIMS_INSIGHTS
  ];

  const handleEraseDocument = (): void => {
    dispatch(setCurrentFile(undefined));
  };

  const handleRegenerate = (): void => {
    dispatch(setRegenerateExtraction(true));
  };

  const handleShowHighlights = (): void => {
    dispatch(setShowHighlights(!showHighlights));
  };

  return (
    <div className={styles['uploaded-document']}>
      {currentFile !== undefined && (
        <>
          <h3>Document uploaded</h3>

          <UploadedFileCard file={currentFile} />
        </>
      )}

      <h3>Selected areas</h3>

      <div className={styles.areas}>
        <div className={styles.chips}>
          {businessArea !== undefined && (
            <Chip
              value={businessAreas[businessArea].name}
              id={businessArea}
              icon="Edit"
              selected
              className={styles['chip-area']}
            />
          )}
          {service !== undefined && (
            <Chip
              value={services[service].name}
              id={service}
              icon="Package"
              selected
              className={styles['chip-service']}
            />
          )}
        </div>
        <ActionButtons
          handleDelete={handleEraseDocument}
          showUploadNewFile={true}
          handleRegenerate={handleRegenerate}
          showHighlights={showHighlights}
          handleShowHighlights={
            service && usesHighlights.includes(service)
              ? handleShowHighlights
              : undefined
          }
        />
      </div>
    </div>
  );
};
